import React, {useMemo, useRef} from 'react';
import {format, subDays} from "date-fns";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {UseFetchData} from "../hooks/use-fetch-data.js";
import {resetRequiredFieldError} from "../../../../utils/project-utils.js";
import ItemToken from "./item-token.jsx";
import {ENCOUNTER_STATUS_OPTIONS} from "../../../../utils/enums/encounter-status-enum.js";
import {getAnalysisItems, itemUsesReasons} from "./analysis-items.js";

const PRESETS = [
    {id: "hoje", label: "Hoje", days: 0},
    {id: "7d", label: "7 dias", days: 7},
    {id: "30d", label: "30 dias", days: 30},
];

const todayStr = () => format(new Date(), "yyyy-MM-dd");
const daysAgoStr = (days) => format(subDays(new Date(), days), "yyyy-MM-dd");

const QueryBuilderModal = (props) => {
    const {open, setOpen} = props;

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={"Montar análise"}
            isCloseClick={false}
            overflow
            className="w-250 max-w-[95vw]"
            headerClass="!text-[#16233a]"
        >
            <BuilderContent {...props}/>
        </Modal>
    );
};

export const BuilderContent = ({filters, setFilters, target, setTarget, focusKey = "", setFocusKey = () => {}, resetFilters, apply, hasDispatch, boName, invalidReasonKeys = [], clearInvalidReason = () => {}}) => {
    const items = useMemo(() => getAnalysisItems(hasDispatch, boName), [hasDispatch, boName]);
    const modules = useMemo(() => hasDispatch ? ["sentry", "dispatch"] : ["sentry"], [hasDispatch]);
    const {reasons, cities, type_place, layers, points, protection_programs} = UseFetchData(modules);

    const builderRef = useRef(null);
    const activeFocusKey = focusKey || target.at(-1) || items[0]?.key;
    const focusItem = items.find(item => item.key === activeFocusKey) ?? items[0];

    const activePreset = useMemo(() => {
        if (filters.dt_end !== todayStr()) return "custom";
        const preset = PRESETS.find(p => filters.dt_ini === daysAgoStr(p.days));
        return preset ? preset.id : "custom";
    }, [filters.dt_ini, filters.dt_end]);

    const handleTarget = (key) => {
        if (!hasDispatch) return;

        if (target.includes(key)) clearInvalidReason(key);
        setFocusKey(key);
        setTarget(prev => prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]);
    };

    const handleUniversalCheckbox = (element) => {
        const {id, checked} = element.target;
        setFilters(prev => ({...prev, [id]: checked}));
    };

    const handleUniversalSelect = (value, key) => setFilters(prev => ({...prev, [key]: value}));

    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null;
        setFilters(prev => ({...prev, [id]: dateFormated}));
    };

    const setPreset = (preset) => setFilters(prev => ({...prev, dt_ini: daysAgoStr(preset.days), dt_end: todayStr()}));

    const setItemFilter = (itemKey, field, value) => setFilters(prev => ({
        ...prev,
        items: {...prev.items, [itemKey]: {...(prev.items[itemKey] ?? {}), [field]: value}}
    }));

    const searchDisabled = !target.length;

    const clearRequiredErrors = () => {
        builderRef.current?.querySelectorAll("[data-required-invalid='true']")
            .forEach(resetRequiredFieldError);
    };

    const renderDetail = (item) => {
        const itemFilters = filters.items[item.key] ?? {};
        const reasonOptions = item.module === "sentry" ? (reasons ?? []).filter(reason => reason.sentry) : (reasons ?? []).filter(reason => reason.dispatch);

        return (
            <div>
                <div className="flex items-center gap-2.5 mb-1">
                    <ItemToken item={item}/>
                    <span className="text-[14px] font-semibold text-[#16233a]">Configuração de {item.name}</span>
                </div>

                <div className="space-y-3 mt-3">
                    {
                        itemUsesReasons(item.key) &&
                        <SelectComponent
                            key={item.key}
                            id={item.key}
                            label="Naturezas *"
                            inputId={`${item.key}`}
                            required
                            isMulti
                            options={reasonOptions}
                            value={itemFilters.reasons ?? []}
                            invalid={target.includes(item.key) && invalidReasonKeys.includes(item.key)}
                            openMenuOnFocus={false}
                            menuPortalZIndex={1050}
                            onChange={(value) => {
                                setItemFilter(item.key, "reasons", value);
                                if (value?.length) clearInvalidReason(item.key);
                            }}
                        />
                    }

                    {
                        item.key === "sentry-occurrence" &&
                        <>
                            <SelectComponent
                                label="Município do fato"
                                options={cities ?? []}
                                value={itemFilters.cityFact}
                                onChange={(value) => setItemFilter(item.key, "cityFact", value)}
                            />
                            <InputCheckbox
                                id="only_my_city"
                                className="analysis-checkbox"
                                label="Apenas cadastradas pela minha corporação"
                                checked={!!itemFilters.only_my_city}
                                onChange={(element) => setItemFilter(item.key, "only_my_city", element.target.checked)}
                            />
                        </>
                    }

                    {
                        item.key === "sos" &&
                        <>
                            <SelectComponent
                                label="Tipo de proteção"
                                isMulti
                                options={protection_programs}
                                value={itemFilters?.protection_type ?? []}
                                onChange={(value) => setItemFilter(item.key, "protection_type", value)}
                            />
                            <SelectComponent
                                label="Status"
                                options={[{value:"ACTIVATED", label: "Ativo"}, {value:"INACTIVATED", label: "Inativo"}]}
                                value={itemFilters.status}
                                onChange={(value) => setItemFilter(item.key, "status", value)}
                            />
                        </>
                    }

                    {
                        item.key === "encounter" &&
                        <>
                            <SelectComponent
                                label="Status"
                                options={ENCOUNTER_STATUS_OPTIONS}
                                value={itemFilters.status}
                                onChange={(value) => setItemFilter(item.key, "status", value)}
                            />
                        </>
                    }
                </div>
            </div>
        );
    };

    return (
        <div ref={builderRef}>
            <Modal.Body className={"flex flex-col max-h-[calc(100vh-8rem)] px-4! pt-4! pb-6! space-y-3"}>
                <div className="bg-[#F6F6F6] border border-[#D9DADA] px-4 py-3 space-y-3 rounded-lg">
                    <div className="flex flex-wrap items-center gap-y-2">
                        <span className="shrink-0 text-[14px] font-bold! tracking-[0.08em] mr-1">Período:</span>

                        <div className="flex gap-1.5">
                            {PRESETS.map(preset => {
                                const on = activePreset === preset.id;
                                return (
                                    <button
                                        key={preset.id}
                                        onClick={() => setPreset(preset)}
                                        className={["h-[30px] px-3 rounded-lg border text-[12px] cursor-pointer transition-colors",
                                            on
                                                ? "border-[#16233a] bg-[#16233a] text-white"
                                                : "border-[#dde4ed] bg-white text-[#6b7a92] hover:border-[#9fb2cd]"].join(" ")}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-2 ml-7">
                            <div className="w-[200px]!">
                                <InputDateTime
                                    id="dt_ini"
                                    label="Data inicial *"
                                    selected={filters.dt_ini}
                                    dateFormat="L"
                                    inputClass={"bg-white!"}
                                    addDateOnClick={false}
                                    onChange={(date) => handleDate("dt_ini", date)}
                                />
                            </div>
                            <span className="font-mono text-[11px] text-[#98a3b2] ml-[1px]">até</span>
                            <div className="w-[200px]! ml-[1px]">
                                <InputDateTime
                                    id="dt_end"
                                    label="Data final *"
                                    selected={filters.dt_end}
                                    dateFormat="L"
                                    inputClass={"bg-white!"}
                                    addDateOnClick={false}
                                    onChange={(date) => handleDate("dt_end", date)}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col ml-7">
                            <InputCheckbox
                                id="georeferenced"
                                className="analysis-checkbox"
                                label="Itens com coordenadas"
                                checked={filters.georeferenced}
                                onChange={handleUniversalCheckbox}
                            />
                            <InputCheckbox
                                id="without_coordinates"
                                className="analysis-checkbox"
                                label="Itens sem coordenadas"
                                checked={filters.without_coordinates}
                                onChange={handleUniversalCheckbox}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <SelectComponent
                                label="Tipo de local"
                                isMulti
                                options={type_place ?? []}
                                value={filters.type_place}
                                onChange={(value) => handleUniversalSelect(value, "type_place")}
                            />
                            <SelectComponent
                                label="Setores"
                                isMulti
                                options={layers ?? []}
                                value={filters.layers}
                                onChange={(value) => handleUniversalSelect(value, "layers")}
                            />
                            <SelectComponent
                                label="Pontos"
                                isMulti
                                options={points ?? []}
                                value={filters.points}
                                onChange={(value) => handleUniversalSelect(value, "points")}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-1 min-h-0 flex-col md:flex-row bg-[#F6F6F6] border border-[#D9DADA] rounded-lg">
                    <div className="md:w-[280px] shrink-0 border-b md:border-b-0 md:border-r border-[#D9DADA] overflow-y-auto p-3 max-h-[36vh] md:max-h-[52vh]">
                        <div className={"text-[11px] font-semibold tracking-[0.08em] uppercase text-[#6b7a92] mb-2.5 px-0.5"}>
                            Itens de análise
                        </div>
                        <div className="flex flex-col gap-1.5">
                            {items.map(item => {
                                const selected = target.includes(item.key);
                                const focused = focusItem?.key === item.key;
                                const invalid = itemUsesReasons(item.key) && selected && invalidReasonKeys.includes(item.key);

                                return (
                                    <div
                                        key={item.key}
                                        className={["flex items-stretch rounded-[9px] border overflow-hidden transition-colors h-[42px] hover:bg-[#EEF2FB]! py-1",
                                            invalid ? "!border-red-500 bg-red-50" : focused ? "border-[#16233a] bg-[#f7f9fb]" : "border-[#e8edf3] bg-white", selected ? "bg-[#EEF2FB]!" : ""].join(" ")}
                                    >
                                        <div className={["w-11 border-r flex items-center justify-center ", invalid ? "!border-red-500" : focused ? "border-[#16233a]" : "border-[#c8d2df]"].join(" ")}>
                                            <InputCheckbox
                                                checked={selected}
                                                onChange={() => handleTarget(item.key)}
                                                disabled={!hasDispatch}
                                            />
                                        </div>
                                        <button
                                            onClick={() => setFocusKey(item.key)}
                                            className="flex-1 min-w-0 flex items-center gap-2.5 p-2.5 text-left cursor-pointer "
                                        >
                                            <ItemToken item={item}/>

                                            <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-[#16233a]">{item.name}</span>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex-1 min-w-0 overflow-y-auto p-4 max-h-[45vh] md:max-h-[52vh]">
                        {renderDetail(focusItem)}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className={"items-center gap-3"}>
                <Button target="primary-outline" onClick={() => {
                    clearRequiredErrors();
                    resetFilters();
                }}>Limpar</Button>
                <Button target={"primary"} onClick={apply} disabled={searchDisabled}>
                    Pesquisar
                </Button>
            </Modal.Footer>
        </div>
    );
};

export default QueryBuilderModal;